import {IPsoWorkerAgreementFormFields, IPsoWorkerAgreementViewModel} from "./pso-worker-agreement.view-model.interface";
import {FormFields} from "../../../../../../models/forms/form-field.interface";
import {NullableString} from "../../../../../../types/nullable-types";
import {MaturePassengerSessionStorageKeys} from "../../../passenger/mature-passenger-session-storage-keys";
import {IPsoWorkerAgreementRequest} from "../../../../../airline-webapi/requests/pso-worker-agreement.request";
import {PsoAgreementModelBase} from "../base/pso-agreement.model";
import {
    IPsoAgreementCommonPersistedFields
} from "../base/pso-agreement.view-mode.interface";
import React from "react";
import {
    PsoWorkerAgreementComponent
} from "../../../../../../components/territorial-continuity-regime/agreements/workers/pso-worker-agreement.component";


export class PsoWorkerAgreementModel extends PsoAgreementModelBase<IPsoWorkerAgreementFormFields> implements IPsoWorkerAgreementViewModel {


    protected isDeclarationDownloaded(): boolean {
        return this.passenger.booking.pso.workersAgreements.downloaded;
    }

    protected _createFields(): FormFields<IPsoWorkerAgreementFormFields> {
        const language = this.services.language;
        const persistedFields = this._getPersistFields();
        return {

            ...this._createCommonFields(persistedFields),

            jobTitle: this._createField<string>({
                fieldName: () => language.translate('Job title'),
                initialValue: () => persistedFields?.jobTitle,
                maxLength: 100
            }),

            employerCompanyName: this._createField<string>({
                fieldName: () => language.translate('Name of the company / professional practice for which the declarant works'),
                initialValue: () => persistedFields?.employerCompanyName,
                maxLength: 100
            }),

            employerCompanyPhone: this._createPhoneField({
                fieldName: () => language.translate('Company phone number'),
                initialValue: () => persistedFields?.employerCompanyPhone,
                maxLength: 20
            }),

            employerCompanyEmail: this._createField<string>({
                fieldName: () => language.translate('Company e-mail'),
                initialValue: () => persistedFields?.employerCompanyEmail,
                maxLength: 100
            }),

            workActivityDescription: this._createField<string>({
                fieldName: () => language.translate('Work Activity to be carried out (eg. conference speaker, court hearing, technical expert etc)'),
                initialValue: () => persistedFields?.workActivityDescription,
                maxLength: 100
            }),

            workLocationCompanyName: this._createField<string>({
                fieldName: () => language.translationFor('Name of the Company/Entity in {region}').withParams({region: this.marketNameTranslation}),
                initialValue: () => persistedFields?.workLocationCompanyName,
                maxLength: 100
            }),
            workLocationCompanyAddress: this._createField<string>({
                fieldName: () => language.translationFor('Adress of the Company/Entity in {region}').withParams({region: this.marketNameTranslation}),
                initialValue: () => persistedFields?.workLocationCompanyAddress,
                maxLength: 100
            }),

            workLocationCompanyPhone: this._createPhoneField({
                fieldName: () => language.translationFor('Phone number of the Company/Entity in {region}').withParams({region: this.marketNameTranslation}),
                initialValue: () => persistedFields?.workLocationCompanyPhone,
            }),
            workLocationCompanyEmail: this._createEmailField({
                fieldName: () => language.translationFor('E-mail of the Company in {region}').withParams({region: this.marketNameTranslation}),
                initialValue: () => persistedFields?.workLocationCompanyEmail,
                maxLength: 200
            }),
            workFromDate: this._createField<Date>({
                fieldName: () => language.translate('From'),
                initialValue: () => this.services.time.tryConvertToDate(persistedFields?.workFromDate)
            }),

            workUntilDate: this._createField<Date>({
                fieldName: () => language.translate('Until'),
                initialValue: () => this.services.time.tryConvertToDate(persistedFields?.workUntilDate),
                validate: field => {
                    if(!field.value) {
                        return null;
                    }
                    if(field.value.getTime() < this.services.time.currentDate.getTime()) {
                        return language.translate('Must be in the future');
                    }

                    if(!this.fields.workFromDate.value) {
                        return null;
                    }

                    if(field.value.getTime() < this.fields.workFromDate.value.getTime()) {
                        return language.translate('Must be after the start date');
                    }

                    return null;
                }
            })

        }
    }


    protected _resetConfirmation() {
        this.passenger.booking.pso.workersAgreements.confirmed = false;
    }

    protected _persistFields(): void {
        const persistedFields: IPsoWorkerAgreementPersistedFields = {
            ...this._persistCommonFields(),

            jobTitle: this.fields.jobTitle.value,
            employerCompanyName: this.fields.employerCompanyName.value,
            employerCompanyPhone: this.fields.employerCompanyPhone.value,
            employerCompanyEmail: this.fields.employerCompanyEmail.value,

            workActivityDescription: this.fields.workActivityDescription.value,
            workLocationCompanyName: this.fields.workLocationCompanyName.value,
            workLocationCompanyAddress: this.fields.workLocationCompanyAddress.value,
            workLocationCompanyPhone: this.fields.workLocationCompanyPhone.value,
            workLocationCompanyEmail: this.fields.workLocationCompanyEmail.value,
            workFromDate: this.fields.workFromDate.value?.toISOString() ?? null,
            workUntilDate: this.fields.workUntilDate.value?.toISOString() ?? null,
        }

        this.passenger.storage.setJson(MaturePassengerSessionStorageKeys.psoWorkAgreement, persistedFields);
    }

    private _getPersistFields(): IPsoWorkerAgreementPersistedFields | null {
        return this.passenger.storage.getJson<IPsoWorkerAgreementPersistedFields>(MaturePassengerSessionStorageKeys.psoWorkAgreement) ?? null;
    }


    buildApiRequest(): IPsoWorkerAgreementRequest {
        return {
            versionNumber: 2,
            declarationDate: this.declarationDate.toISOString(),
            psoMarketName: this.passenger.booking.pso.psoMarket?.marketName ?? "",

            firstName: this.passengerFirstName,
            lastName: this.passengerLastName,
            dateOfBirth: this.services.time.formatYYYY_MM_DD(this.fields.dateOfBirth.value),
            passengerBirthLocation: this.fields.birthLocation.value!,
            passengerResidenceLocation: this.fields.residenceLocation.value!,
            passengerFiscalCode: this.fields.fiscalCode.value!,
            passengerPhoneNumber: this.fields.phoneNumber.value!,
            passengerEmail: this.fields.email.value!,

            jobTitle: this.fields.jobTitle.value!,
            employerCompanyName: this.fields.employerCompanyName.value!,
            employerCompanyPhone: this.fields.employerCompanyPhone.value!,
            employerCompanyEmail: this.fields.employerCompanyEmail.value!,

            workLocationCompanyName: this.fields.workLocationCompanyName.value!,
            workLocationCompanyPhone: this.fields.workLocationCompanyPhone.value!,
            workLocationCompanyEmail: this.fields.workLocationCompanyEmail.value!,

            workActivityDescription: this.fields.workActivityDescription.value!,
            workLocationCompanyAddress: this.fields.workLocationCompanyAddress.value!,
            workFromDate: this.services.time.formatYYYY_MM_DD(this.fields.workFromDate.value),
            workUntilDate: this.services.time.formatYYYY_MM_DD(this.fields.workUntilDate.value),
        }
    }

    renderComponent(): React.ReactElement {
        return (
            <PsoWorkerAgreementComponent workerAgreement={this}/>
        );
    }
}

interface IPsoWorkerAgreementPersistedFields extends IPsoAgreementCommonPersistedFields {

    jobTitle: NullableString;
    employerCompanyName: NullableString;
    employerCompanyPhone: NullableString;
    employerCompanyEmail: NullableString;

    workActivityDescription: NullableString;
    workLocationCompanyName: NullableString;
    workLocationCompanyAddress: NullableString;
    workLocationCompanyPhone: NullableString;
    workLocationCompanyEmail: NullableString;
    workFromDate: NullableString;
    workUntilDate: NullableString;

}